import { DESIGN_TOKENS, IconBrandShield, IconKey, IconMyPlayerLogo } from '@myplayer/ui';
import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { initializeAdminTheme } from './App.js';
import { useAuth } from './auth-context.js';
import {
  getBrandedAuthEmailTemplates,
  type FirebaseEmailTemplateId,
} from './auth-email-templates.js';
import { Badge, Button, Card, Input, ModalDialog } from './components/ui/primitives.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';
import { resolveAdminEmailPlaceholder } from './use-workspace-identifiers.js';

export function AuthScreenShell(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly badgeLabel?: string;
  readonly children: ReactNode;
}): ReactElement {
  useEffect(() => {
    initializeAdminTheme();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-page text-fg" data-testid="branded-auth-shell">
      <div className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-[440px]">
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-2 flex items-center gap-2">
              <IconMyPlayerLogo size={28} className="shrink-0" />
              <span className="text-[17px] font-bold leading-tight tracking-[-0.2px] text-fg">
                {DESIGN_TOKENS.brandIdentity.logoText}
              </span>
            </div>
            <span className="text-[12px] font-medium text-fg-secondary">
              {DESIGN_TOKENS.brandIdentity.logoSubtitle}
            </span>
          </div>

          <Card className="w-full p-5 shadow-card-base sm:p-6">
            <div className="mb-5 text-center">
              <h1 className="text-[20px] font-semibold tracking-tight text-fg">{props.title}</h1>
              {props.subtitle && (
                <p className="mt-1 text-[13px] text-fg-secondary">{props.subtitle}</p>
              )}
            </div>
            {props.children}
          </Card>
        </div>
      </div>
    </div>
  );
}

export function BrandedEmailPreviewCard(props: {
  readonly templateId: FirebaseEmailTemplateId;
  readonly recipientEmail?: string;
}): ReactElement | null {
  const template = useMemo(
    () => getBrandedAuthEmailTemplates().find((item) => item.id === props.templateId) ?? null,
    [props.templateId],
  );
  if (!template) return null;

  return (
    <div
      data-testid={`branded-email-preview-${props.templateId}`}
      className="mt-4 rounded-card border border-subtle bg-surface-subtle p-4 text-left"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-subtle pb-2">
        <div className="flex items-center gap-2">
          <IconMyPlayerLogo size={18} />
          <span className="text-[12px] font-semibold text-fg">{template.subject}</span>
        </div>
        <Badge variant="outline" size="sm">
          Branded Email
        </Badge>
      </div>
      <p className="text-[12px] text-fg-secondary">{template.bodyIntro}</p>
      {props.recipientEmail && props.recipientEmail.trim().length > 0 && (
        <p className="mt-1.5 text-[11px] text-fg-secondary">
          Recipient: <span className="font-semibold text-fg">{props.recipientEmail.trim()}</span>
        </p>
      )}
      <p className="mt-2 text-[11px] text-fg-muted">{template.expiryAndSafetyNote}</p>
    </div>
  );
}

export function ChangePasswordModal(props: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onPasswordChanged?: () => void;
}): ReactElement | null {
  const { session, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  if (!props.open) return null;

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setLocalError(null);
    if (currentPassword.trim().length === 0) {
      setLocalError('Please enter your current password.');
      return;
    }
    if (newPassword.trim().length < 8) {
      setLocalError('Please choose a new password with at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Your new password and confirmation password do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      props.onPasswordChanged?.();
      props.onClose();
    } catch (err: unknown) {
      setLocalError(formatPlainAdminError(err, ADMIN_STRINGS.errors.changePasswordFallback));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalDialog
      open={props.open}
      title="Change password"
      subtitle={session?.email ?? resolveAdminEmailPlaceholder('admin')}
      icon={<IconKey />}
      maxWidthClassName="max-w-lg"
      submitting={submitting}
      onClose={props.onClose}
      data-testid="change-password-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={props.onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="change-password-form"
            variant="primary"
            size="sm"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save password'}
          </Button>
        </>
      }
    >
      <form id="change-password-form" onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div className="space-y-1.5">
          <label
            htmlFor="change-current-password"
            className="block text-[12px] font-medium text-fg-secondary"
          >
            Current password
          </label>
          <Input
            id="change-current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
          />
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="change-new-password"
            className="block text-[12px] font-medium text-fg-secondary"
          >
            New password
          </label>
          <Input
            id="change-new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
          />
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="change-confirm-password"
            className="block text-[12px] font-medium text-fg-secondary"
          >
            Confirm new password
          </label>
          <Input
            id="change-confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter new password"
          />
        </div>

        {localError !== null && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {localError}
          </p>
        )}
      </form>
    </ModalDialog>
  );
}

export function ChangePasswordPage(): ReactElement {
  const navigate = useNavigate();
  const [savedNotice, setSavedNotice] = useState(false);

  return (
    <AuthScreenShell
      title="Change your password"
      subtitle="Update the password you use to sign in to the MyPlayer Admin Console."
      badgeLabel="Account Security"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2.5 rounded-card border border-subtle bg-surface-subtle p-3 text-[12px] text-fg-secondary">
          <IconBrandShield />
          <span>Choose a strong password with at least 8 characters.</span>
        </div>
        {savedNotice ? (
          <div className="space-y-3" data-testid="change-password-page-saved">
            <p className="rounded-control border border-status-valid-border bg-status-valid-bg p-3 text-[12px] text-status-valid">
              Your password has been updated.
            </p>
            <Button variant="primary" className="w-full" onClick={() => navigate('/settings')}>
              Return to settings
            </Button>
          </div>
        ) : (
          <ChangePasswordModal
            open={true}
            onClose={() => navigate('/settings')}
            onPasswordChanged={() => setSavedNotice(true)}
          />
        )}
      </div>
    </AuthScreenShell>
  );
}
